import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, MapPin, BookOpen, Briefcase, Star, ExternalLink } from 'lucide-react';
import api from '../../utils/api';

export default function AdminPerfilCandidato() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/candidatos/${id}`)
      .then(r => setP(r.data))
      .catch(() => navigate('/admin/candidatos'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-acre-green border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!p) return null;

  const pct = p.porcentagem_perfil ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-acre-dark mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        {/* Card principal */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-acre-green to-teal-600 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl font-bold text-white">
                {p.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="font-sora font-bold text-xl text-white">{p.nome}</h1>
                  {p.selo_verificado && <span className="text-acre-gold text-lg">✓</span>}
                </div>
                <p className="text-white/70 text-sm">{p.email}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${p.ativo ? 'bg-emerald-400/20 text-emerald-200' : 'bg-red-400/20 text-red-200'}`}>
                    {p.ativo ? '● Ativo' : '○ Inativo'}
                  </span>
                  {p.disponibilidade && (
                    <span className="text-xs bg-white/10 text-white/80 px-2.5 py-1 rounded-full">{p.disponibilidade}</span>
                  )}
                </div>
              </div>
              {/* % perfil */}
              <div className="text-center">
                <div className="relative w-14 h-14">
                  <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                    <circle cx="28" cy="28" r="22" stroke="white" strokeOpacity="0.2" strokeWidth="5" fill="none"/>
                    <circle cx="28" cy="28" r="22" stroke="white" strokeWidth="5" fill="none"
                      strokeDasharray={`${(pct/100)*138.2} 138.2`} strokeLinecap="round"/>
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-xs">{pct}%</span>
                </div>
                <p className="text-white/60 text-xs mt-1">Perfil</p>
              </div>
            </div>
          </div>

          <div className="p-6 grid sm:grid-cols-2 gap-4">
            {[
              { icon: <MapPin className="w-4 h-4"/>, label: 'Cidade', val: p.cidade },
              { icon: <Phone className="w-4 h-4"/>, label: 'Telefone', val: p.telefone || p.whatsapp },
              { icon: <BookOpen className="w-4 h-4"/>, label: 'Escolaridade', val: p.nivel_escolaridade },
              { icon: <Briefcase className="w-4 h-4"/>, label: 'Experiência', val: p.experiencia_anos ? `${p.experiencia_anos} anos` : null },
            ].filter(i => i.val).map((i, k) => (
              <div key={k} className="flex items-center gap-3 text-sm">
                <span className="text-acre-green">{i.icon}</span>
                <div><p className="text-gray-400 text-xs">{i.label}</p><p className="text-acre-dark font-medium">{i.val}</p></div>
              </div>
            ))}

            {p.areas_interesse?.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-gray-400 text-xs mb-2">Áreas de Interesse</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.areas_interesse.map(a => (
                    <span key={a} className="bg-acre-light text-acre-green text-xs font-medium px-2.5 py-1 rounded-full">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {p.resumo_profissional && (
              <div className="sm:col-span-2">
                <p className="text-gray-400 text-xs mb-1">Resumo Profissional</p>
                <p className="text-gray-600 text-sm leading-relaxed">{p.resumo_profissional}</p>
              </div>
            )}

            {(p.video_pitch_url || p.link_portfolio) && (
              <div className="sm:col-span-2 flex gap-3">
                {p.video_pitch_url && (
                  <a href={p.video_pitch_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs text-acre-green hover:underline">
                    <ExternalLink className="w-3.5 h-3.5"/> Vídeo Pitch
                  </a>
                )}
                {p.link_portfolio && (
                  <a href={p.link_portfolio} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs text-acre-green hover:underline">
                    <ExternalLink className="w-3.5 h-3.5"/> Portfólio
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Formação */}
        {p.formacao?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
            <h2 className="font-sora font-bold text-acre-dark mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-acre-green"/> Formação Acadêmica
            </h2>
            <div className="space-y-3">
              {p.formacao.map((f, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full bg-acre-green mt-1.5 flex-shrink-0"/>
                  <div>
                    <p className="font-medium text-acre-dark">{f.curso}</p>
                    <p className="text-gray-400">{f.instituicao} {f.ano_conclusao && `· ${f.ano_conclusao}`}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Experiências */}
        {p.experiencias?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-sora font-bold text-acre-dark mb-4 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-acre-green"/> Experiências
            </h2>
            <div className="space-y-4">
              {p.experiencias.map((e, i) => (
                <div key={i} className="flex gap-3 text-sm border-b border-gray-50 last:border-0 pb-3 last:pb-0">
                  <span className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"/>
                  <div>
                    <p className="font-medium text-acre-dark">{e.cargo}</p>
                    <p className="text-gray-500">{e.empresa}</p>
                    {e.descricao && <p className="text-gray-400 text-xs mt-1 leading-relaxed">{e.descricao}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
